import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, RefreshCw } from "lucide-react";
import { useTranslation } from "@/i18n/translations";

import { API } from "@/App";
const RISK_CLS = { low: "bg-green-100 text-green-700", medium: "bg-amber-100 text-amber-700", high: "bg-red-100 text-red-700" };

export default function ClientAiSummary({ phoneKey }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/clients/${phoneKey}/summary`).then((r) => setData(r.data?.exists ? r.data : null)).catch(() => {});
  }, [phoneKey]);

  const build = async (force) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/clients/${phoneKey}/summary${force ? "?force=true" : ""}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="my-3 p-3 rounded-lg border-2 border-dashed border-purple-300 bg-purple-50/60" data-testid={`client-ai-summary-${phoneKey}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="font-bold text-sm flex items-center gap-1"><Sparkles size={14} /> {t("clients.aiSummary")}</p>
        <button type="button" disabled={busy} onClick={() => build(!!data)} data-testid={`client-ai-summary-btn-${phoneKey}`}
          className="text-xs font-semibold px-3 py-1 rounded-full bg-black text-white disabled:opacity-50 flex items-center gap-1">
          {busy ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}
          {busy ? t("clients.aiSummaryBusy") : data ? t("clients.aiSummaryRefresh") : t("clients.aiSummaryBuild")}
        </button>
      </div>
      {!data && !busy && <p className="text-xs text-gray-500 mt-1">{t("clients.aiSummaryHint")}</p>}
      {data && (
        <div className="mt-2 text-sm space-y-2">
          <div className="flex flex-wrap gap-1">
            {(data.tags || []).map((tag) => <span key={tag} className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-purple-200">{tag}</span>)}
            {data.risk && <span className={`text-[11px] px-2 py-0.5 rounded-full ${RISK_CLS[data.risk]}`}>{t("clients.aiRisk")}: {data.risk}{data.risk_reason ? ` — ${data.risk_reason}` : ""}</span>}
          </div>
          <p>{data.summary}</p>
          {data.visit_rhythm && <p><span className="text-gray-500 text-xs">{t("clients.aiRhythm")}:</span> {data.visit_rhythm}</p>}
          {data.favorite_services?.length > 0 && <p><span className="text-gray-500 text-xs">{t("clients.aiFavorites")}:</span> {data.favorite_services.join(", ")}</p>}
          {data.preferences?.length > 0 && (
            <ul className="list-disc ml-5 text-xs text-gray-700">{data.preferences.map((p, i) => <li key={i}>{p}</li>)}</ul>
          )}
          {data.next_offer && (
            <p className="p-2 rounded bg-white border border-purple-200"><span className="font-semibold">💡 {t("clients.aiNextOffer")}:</span> {data.next_offer}</p>
          )}
          <p className="text-[10px] text-gray-400">{t("clients.aiGenerated")}: {new Date(data.generated_at).toLocaleString()}</p>
        </div>
      )}
    </div>
  );
}
